import { Box, Button, Card, Group, Stack, Text, useMatches } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { TextSerif } from "../TextSerif";

import mobileAppIllustration from "@/assets/illustrations/app-mobile.svg";
import patternIllustration from "@/assets/illustrations/pattern.svg";
import { IconArrowRight } from "@/components/icons";
import { global_settings } from "@/config";

import type { MantineStyleProps } from "@mantine/core";
import type { FC } from "react";

export type BlueBannerProps = {} & MantineStyleProps;

export const BlueBanner: FC<BlueBannerProps> = ({ ...props }) => {
  const { t } = useTranslation();
  const [isButtonHovered, setIsButtonHovered] = useState(false);

  const isMobile = useMatches({ base: true, md: false });

  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  if (!global_settings.enableMobileAppBlueBanner) return null;

  const height = isMobile ? "fit-content" : 220;
  const titleSize = isMobile ? "1.5em" : "2em";

  const descriptionSize = isMobile ? "0.75em" : "sm";
  const descriptionWidth = isMobile ? 300 : 500;
  const descriptionLineHeight = isMobile ? "sm" : "1.4em";

  const gradientBG = isMobile
    ? `linear-gradient(to right,
          #102E8E 0%,
          #1438af99 45%,
          #102d8e29 100%
        ), url(${patternIllustration})`
    : /* Desktop */
      `linear-gradient(to right,
          #102E8E 0%,
          #102d8eb8 30%,
          #102d8e36 50%,
          ${hexToRgba("#0c236a", 0.2)} 100%
        ), url(${patternIllustration})`;

  return (
    <Card
      w="100%"
      p={{
        base: "xs",
        sm: "md",
      }}
      h={height}
      style={{
        position: "relative",
        overflow: "hidden",
        backgroundImage: gradientBG,
        backgroundRepeat: "no-repeat, no-repeat",
        backgroundPosition: "center",
        backgroundSize: "cover, cover",
      }}
      {...props}
    >
      <Group
        h="100%"
        justify={isMobile ? "space-between" : "space-between"}
        align={isMobile ? "stretch" : "center"}
        gap="xl"
        wrap={isMobile ? "wrap" : "nowrap"}
        style={{ position: "relative", zIndex: 1 }}
      >
        <Stack
          gap={isMobile ? "md" : "lg"}
          p="lg"
          style={{ flex: 1, position: "relative", zIndex: 2 }}
        >
          <Text component="h2" c="white" fz={titleSize} lh="1.25em" fw={500}>
            {t("components.BlueBanner.title")}
          </Text>

          <Box maw={350} w={descriptionWidth}>
            <TextSerif
              component="p"
              fz={descriptionSize}
              fw={400}
              lh={descriptionLineHeight}
              c="white"
            >
              {t("components.BlueBanner.fullAccess")}
            </TextSerif>
          </Box>

          <Box w={isMobile ? 200 : "auto"}>
            <Button
              variant="white"
              c="dark"
              rightSection={
                <Box
                  component="span"
                  style={{
                    display: "flex",
                    transform: isButtonHovered ? "translateX(2px)" : "translateX(0)",
                    transition: "transform 200ms ease",
                  }}
                >
                  <IconArrowRight />
                </Box>
              }
              onMouseEnter={() => setIsButtonHovered(true)}
              onMouseLeave={() => setIsButtonHovered(false)}
            >
              {t("components.BlueBanner.downloadApp")}
            </Button>
          </Box>
        </Stack>

        <Box
          component="img"
          src={mobileAppIllustration}
          alt="Mobile banner image"
          style={{
            position: "absolute",
            right: isMobile ? -20 : -10,
            bottom: isMobile ? -8 : 20,
            top: 0,
            alignSelf: "flex-end",
            width: "auto",
            height: isMobile ? 100 : 220,
            maxWidth: "none",
            zIndex: 0,
          }}
        />
      </Group>
    </Card>
  );
};
